import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query'
import type { Row } from '@solum/sdk'
import {
  ActivityComposer, Button, Modal, type ActivityComposerContext,
  type ActivityComposerDraft, type ActivityComposerKind, useToast,
} from '@solum/ui'
import { useSolum } from '../data/client'
import {
  displayLabel, invalidateRecordSurfaces, reconcileRelationships, useLookupLabels,
} from '../data/queries'
import { RefPicker } from '../form/RefPicker'
import { me } from '../experience/governed'
import { useRegionalPrefs } from '../data/userFormat'
import { currentTimezone, timezoneChoices } from '../data/timezones'
import s from './ActivityComposerHost.module.css'
import { optimisticRows } from '../data/optimistic'

export interface ActivityComposerRequest {
  kind?: ActivityComposerKind
  context?: ActivityComposerContext | null
}

type Listener = (request: ActivityComposerRequest) => void
const listeners = new Set<Listener>()

/** One event seam for Command Marble and the drawer. It opens one host and one
 * shared composer; neither entry point owns a second form or write path. */
export function openActivityComposer(request: ActivityComposerRequest = {}) {
  for (const listener of listeners) listener(request)
}

function subscribe(listener: Listener) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function localWallTimeToIso(date: string, time: string, timezone: string): string {
  const naive = new Date(`${date}T${time}:00Z`)
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(naive)
  const value = (kind: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === kind)?.value ?? 0)
  const represented = Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute'), value('second'))
  return new Date(naive.getTime() - (represented - naive.getTime())).toISOString()
}

export type ActivityRpc = <T>(fn: string, args: Record<string, unknown>, schema: string) => Promise<T>

interface OptimisticActivity {
  commit(serverId: string): void
  rollback(): void
}

export interface ActivityMutationVariables {
  draft: ActivityComposerDraft
  assignee: string | null
  participants: string[]
  optimisticId?: string
  now?: string
}

interface ActivityMutationContext {
  optimistic: OptimisticActivity
  committed: boolean
}


/**
 * Put a submitted activity into every relevant cache that is already on screen.
 * The database remains authoritative: success replaces the temporary id and
 * immediately starts a refetch; refusal restores the exact prior answers.
 *
 * Each kind is staged in its own source cache. Nothing is fabricated as an
 * activity: tasks remain core work and meeting reports remain engagements.
 */
export function stageOptimisticActivity(
  qc: QueryClient,
  draft: ActivityComposerDraft,
  assignee: string | null,
  participants: readonly string[] = [],
  optimisticId = `pending:${crypto.randomUUID()}`,
  now = new Date().toISOString(),
): OptimisticActivity {
  /* The mechanism is shared (#2018 item 5); what stays here is the DOMAIN —
     which caches each kind touches and what its row looks like. */
  const rows = optimisticRows(qc, optimisticId)
  const stage = (key: QueryKey, row: Row) => rows.stage(key, row)

  if (draft.kind === 'task') {
    const row: Row = {
      id: optimisticId,
      entity_type: 'core_task',
      module: 'core',
      title: draft.subject,
      body: draft.body || null,
      status: 'saving',
      due_at: draft.dueDate
        ? localWallTimeToIso(draft.dueDate, '17:00', Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC')
        : null,
      assignee_kind: 'core_person',
      assignee_id: assignee,
      optimistic: true,
    }
    stage(['my-work'], row)
    if (draft.about) stage(['contextual-tasks', draft.about.entity, draft.about.id], row)
  } else if (draft.kind === 'meeting_report') {
    if (draft.about) stage(['contextual-meeting-reports', draft.about.entity, draft.about.id], {
      id: optimisticId,
      title: draft.subject,
      description: draft.body || null,
      status: 'saving',
      scheduled_at: now,
      owner_id: me(),
      source_kind: draft.meetingSource === 'manual' ? 'standalone' : 'provider',
      source_system: draft.meetingSource === 'manual' ? null : draft.meetingSource,
      created_at: now,
      optimistic: true,
    })
  } else {
    const occurredAt = draft.kind === 'appointment' ? null : now
    const dueAt = draft.kind === 'appointment'
      ? localWallTimeToIso(draft.startDate, draft.startTime, draft.timezone)
      : null
    const row: Row = {
      id: optimisticId,
      activity_id: optimisticId,
      channel: draft.kind,
      direction: draft.kind === 'note' ? 'internal' : draft.direction,
      status: 'saving',
      subject: draft.subject,
      body: draft.body || null,
      occurred_at: occurredAt,
      due_at: dueAt,
      owner_id: me(),
      optimistic: true,
    }
    /* The drawer's book lenses (#2444 slice 4): the team lens and each
       caller's "mine" lens are separate cached pages under one prefix, now
       ordered server-side. The row belongs in the team book and in the
       author's own — never in somebody else's "mine". */
    rows.stageMatching(['related', 'activity', 'activity'], row, (key) => {
      const where = key[3] as Row | undefined
      return where == null || where.owner_id == null || where.owner_id === row.owner_id
    })
    if (draft.about) {
      /* One timeline is cached once per focused participant, so the same row
         belongs in some of those copies and not others. */
      rows.stageMatching(['contextual-timeline', draft.about.entity, draft.about.id], row,
        (key) => key[3] == null || participants.includes(String(key[3])))
    }
  }

  /* The settled status is the one thing the lifecycle cannot know: it is what
     THIS kind becomes once the server has it. */
  const settled = draft.kind === 'task' ? 'open'
    : draft.kind === 'meeting_report' ? 'held'
      : draft.kind === 'appointment' ? 'scheduled'
        : 'completed'

  return {
    commit: (serverId: string) => rows.commit(serverId, { status: settled }),
    rollback: () => rows.rollback(),
  }
}

/** Domain routing is a pure contract: one composer does not mean one weak
 * table. Keeping this outside React also makes every argument and refusal
 * testable without a second fake composer. */
export async function persistActivityDraft(
  rpc: ActivityRpc,
  draft: ActivityComposerDraft,
  assignee: string | null,
  participants: string[],
): Promise<string> {
  const about = draft.about
  if (draft.kind === 'task') {
    const common = {
      p_title: draft.subject,
      p_body: draft.body || null,
      p_assignee_id: assignee,
      p_due_at: draft.dueDate ? localWallTimeToIso(draft.dueDate, '17:00', Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC') : null,
      p_priority: null,
    }
    if (about) return rpc<string>('create_contextual_task', {
      ...common,
      p_subject_type: about.entity,
      p_subject_id: about.id,
    }, 'activity')
    return rpc<string>('create_task', {
      ...common,
      p_assignee_kind: 'core_person',
      p_account_id: null,
      p_tracking_only: false,
      p_origin: { source: 'activity_composer' },
    }, 'core')
  }
  if (draft.kind === 'meeting_report') {
    return rpc<string>('create_meeting_report', {
      p_title: draft.subject,
      p_note: draft.body || null,
      p_account_id: about?.entity === 'core_organization' ? about.id : null,
      p_subject_type: about?.entity ?? null, p_subject_id: about?.id ?? null,
      p_scheduled_at: null, p_duration_min: null, p_location: null,
      p_timezone: null,
      p_source_system: draft.meetingSource === 'manual' ? null : draft.meetingSource,
      p_participants: participants.map((personId) => ({ person_id: personId, role: 'required' })),
    }, 'engagements')
  }
  const dueAt = draft.kind === 'appointment'
    ? localWallTimeToIso(draft.startDate, draft.startTime, draft.timezone)
    : null
  return rpc<string>('create_activity', {
    p_channel: draft.kind,
    p_subject: draft.subject,
    p_body: draft.body || null,
    p_about_type: about?.entity ?? null,
    p_about_id: about?.id ?? null,
    p_participants: participants.map((personId) => ({ person_id: personId, role: 'required' })),
    p_direction: draft.kind === 'note' ? 'internal' : draft.direction,
    p_occurred_at: draft.kind === 'appointment' ? null : new Date().toISOString(),
    p_due_at: dueAt,
    p_attributes: draft.kind === 'appointment' ? {
      timezone: draft.timezone, duration_minutes: draft.durationMinutes,
      location: draft.location || null, join_url: draft.joinUrl || null,
    } : {},
  }, 'activity')
}

function activityCancellationPromises(qc: QueryClient, draft: ActivityComposerDraft): Promise<void>[] {
  if (draft.kind === 'task') {
    return [
      qc.cancelQueries({ queryKey: ['my-work'] }, { revert: false }),
      ...(draft.about ? [qc.cancelQueries({
        queryKey: ['contextual-tasks', draft.about.entity, draft.about.id],
      }, { revert: false })] : []),
    ]
  }
  if (draft.kind === 'meeting_report') {
    return draft.about ? [qc.cancelQueries({
      queryKey: ['contextual-meeting-reports', draft.about.entity, draft.about.id],
    }, { revert: false })] : []
  }
  return [
    qc.cancelQueries({ queryKey: ['related', 'activity', 'activity', {}] }, { revert: false }),
    ...(draft.about ? [qc.cancelQueries({
      queryKey: ['contextual-timeline', draft.about.entity, draft.about.id],
    }, { revert: false })] : []),
  ]
}

function createdActivityEntity(kind: ActivityComposerDraft['kind']): string {
  if (kind === 'task') return 'core_task'
  if (kind === 'meeting_report') return 'engagements_engagement'
  return 'activity_activity'
}

/**
 * The composer's complete TanStack lifecycle. Keeping the callbacks together
 * makes the sequencing reusable and testable: stage in `onMutate`, commit or
 * restore from the returned context, and do not report success until every
 * active surface that can show the write has answered.
 */
export function activityMutationLifecycle(qc: QueryClient, rpc: ActivityRpc) {
  return {
    meta: { errorHandled: true },
    mutationFn: ({ draft, assignee, participants }: ActivityMutationVariables) =>
      persistActivityDraft(rpc, draft, assignee, participants),
    onMutate: async (variables: ActivityMutationVariables): Promise<ActivityMutationContext> => {
      const cancellations = activityCancellationPromises(qc, variables.draft)
      const optimistic = stageOptimisticActivity(
        qc, variables.draft, variables.assignee, variables.participants,
        variables.optimisticId, variables.now,
      )
      try {
        // The card is staged synchronously, while an older in-flight answer is
        // cancelled before the authoritative write is allowed to start.
        await Promise.all(cancellations)
        return { optimistic, committed: false }
      } catch (error) {
        optimistic.rollback()
        throw error
      }
    },
    onSuccess: async (
      id: string, variables: ActivityMutationVariables, context: ActivityMutationContext | undefined,
    ) => {
      context?.optimistic.commit(id)
      if (context) context.committed = true
      const { draft } = variables
      const about = draft.about
      const settling: Promise<void>[] = [
        invalidateRecordSurfaces(qc, createdActivityEntity(draft.kind), id, { work: draft.kind === 'task' }),
        qc.invalidateQueries({ queryKey: ['related', 'activity', 'activity', {}] }),
      ]
      if (about) {
        settling.push(
          // Every contextual write creates a relationship as part of its
          // governed transaction. The record's rows and count are one read
          // model, so success cannot close the composer until both agree.
          reconcileRelationships(qc, about.entity, about.id),
          qc.invalidateQueries({ queryKey: ['contextual-timeline', about.entity, about.id] }),
          qc.invalidateQueries({ queryKey: ['contextual-tasks', about.entity, about.id] }),
          qc.invalidateQueries({ queryKey: ['contextual-meeting-reports', about.entity, about.id] }),
        )
      }
      await Promise.all(settling)
    },
    onError: (_error: unknown, _variables: ActivityMutationVariables, context?: ActivityMutationContext) => {
      // A server refusal restores the snapshot. A later reconciliation error
      // must not erase a write the server has already accepted.
      if (!context?.committed) context?.optimistic.rollback()
    },
  }
}

/** The single submit transaction from the user's point of view: visible now,
 * authoritative when the governed act answers, absent again if it refuses. */
export async function persistActivityWithImmediateFeedback(
  qc: QueryClient,
  rpc: ActivityRpc,
  draft: ActivityComposerDraft,
  assignee: string | null,
  participants: string[],
  optimisticId?: string,
  now?: string,
): Promise<string> {
  const lifecycle = activityMutationLifecycle(qc, rpc)
  const variables = { draft, assignee, participants, optimisticId, now }
  let context: ActivityMutationContext | undefined
  try {
    context = await lifecycle.onMutate(variables)
    const id = await lifecycle.mutationFn(variables)
    await lifecycle.onSuccess(id, variables, context)
    return id
  } catch (error) {
    lifecycle.onError(error, variables, context)
    throw error
  }
}

export function ActivityComposerHost() {
  const { client } = useSolum()
  const qc = useQueryClient()
  const toast = useToast()
  const { prefs } = useRegionalPrefs()
  const [request, setRequest] = useState<ActivityComposerRequest | null>(null)
  const [participants, setParticipants] = useState<string[]>([])
  const [participantCandidate, setParticipantCandidate] = useState<string | null>(null)
  const [assignee, setAssignee] = useState<string | null>(null)
  const labels = useLookupLabels('core_person', participants)
  const {
    mutateAsync: persistActivity,
    isPending: submitting,
    error: mutationError,
    reset: resetMutation,
  } = useMutation(activityMutationLifecycle(
    qc, (fn, args, schema) => client.raw().rpcScalar(fn, args, schema),
  ))
  const resolvedTimezone = currentTimezone(
    prefs.timezone,
    typeof Intl === 'undefined' ? null : Intl.DateTimeFormat().resolvedOptions().timeZone,
  )

  useEffect(() => subscribe((next) => {
    setRequest(next)
    setParticipants([])
    setParticipantCandidate(null)
    setAssignee(null)
    resetMutation()
  }), [resetMutation])

  const participantControl = useMemo(() => (
    <div className={s.people}>
      {participants.length > 0 && <div className={s.chips}>
        {participants.map((id) => {
          const row = labels.byId.get(id) as Row | undefined
          return <span key={id} className={s.chip}>
            {row ? displayLabel(row) : 'Selected person'}
            <Button type="button" size="sm" variant="ghost" aria-label="Remove participant"
              onClick={() => setParticipants((current) => current.filter((item) => item !== id))}>×</Button>
          </span>
        })}
      </div>}
      <RefPicker target={{ entity: 'core_person', label: 'person' }} ariaLabel="Add participant"
        value={participantCandidate} testid="activity-participant"
        onChange={(id) => {
          if (id) setParticipants((current) => current.includes(id) ? current : [...current, id])
          setParticipantCandidate(null)
        }} />
    </div>
  ), [labels.byId, participantCandidate, participants])

  if (!request) return null

  const close = () => { if (!submitting) setRequest(null) }
  const submit = async (draft: ActivityComposerDraft) => {
    try {
      const about = draft.about
      await persistActivity({ draft, assignee, participants })
      toast.success(
        draft.kind === 'task' ? 'Task created'
          : draft.kind === 'appointment' ? 'Appointment scheduled'
            : draft.kind === 'meeting_report' ? 'Meeting report started'
              : 'Activity captured',
        about ? `Linked to ${about.label}.` : 'Created without a linked record.',
      )
      setRequest(null)
    } catch { /* onError owns rollback; the mutation error stays beside the form */ }
  }

  return (
    <Modal label="Create activity" width={760} testid="activity-composer-modal" onClose={close}>
      <div className={s.scroll}>
        <ActivityComposer key={`${request.kind ?? 'task'}:${request.context?.entity ?? ''}:${request.context?.id ?? ''}`}
          initialKind={request.kind} context={request.context}
          timezoneOptions={timezoneChoices(resolvedTimezone)}
          initialTimezone={resolvedTimezone}
          participants={participantControl}
          assignee={<RefPicker target={{ entity: 'core_person', label: 'person' }} ariaLabel="Assignee"
            value={assignee} onChange={setAssignee} testid="activity-assignee" />}
          submitting={submitting}
          error={mutationError instanceof Error
            ? mutationError.message
            : mutationError ? 'The activity was refused.' : null}
          onCancel={close} onSubmit={submit} />
      </div>
    </Modal>
  )
}
